//////////////////////////////////////////////////////////////////////////////
//
//  사용자 검색 및 선택 컴포넌트
//
//  작성자: MediInStep Team
//  생성일: 2025-12-30
//
//  설명: 푸시 캠페인 타겟팅을 위한 사용자 검색 및 선택 UI
//
//////////////////////////////////////////////////////////////////////////////

import { useState, useRef } from "react";
import {
    Box,
    TextField,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Paper,
    Typography,
    CircularProgress,
    Popper,
    ClickAwayListener,
} from "@mui/material";
import { request } from "app/network";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

interface UserSearchSelectorProps {
    selectedUserIds: number[];
    tenantSeqId: number | null;
    darkMode: boolean;
    onSelectionChange: (userIds: number[]) => void;
}

interface User {
    seqId: number;  // Primary key for database relations
    id: string;     // Login ID (e.g., kakao-XXXXX)
    name: string;
    email: string;
    mobile?: string;
}

export default function UserSearchSelector({
    selectedUserIds,
    tenantSeqId,
    darkMode,
    onSelectionChange,
}: UserSearchSelectorProps) {
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);

    // 사용자 검색
    const handleSearch = async (query: string) => {
        setSearchQuery(query);

        if (!query.trim() || !tenantSeqId) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        setLoading(true);
        setShowResults(true);

        try {
            const response = await request("User.pagedList", {
                paramz: {
                    tenantSeqId,
                    searchKeyword: query,
                    pageNumber: 1,
                    pageSize: 20,
                },
            });

            console.log("User search response:", response.data.items);
            setSearchResults(response.data.items || []);
        } catch (error) {
            console.error("User search failed:", error);
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    // 사용자 선택
    const handleSelectUser = (user: User) => {
        console.log("Selecting user:", user);
        console.log("User seqId:", user.seqId);
        if (!selectedUserIds.includes(user.seqId)) {
            const newUserIds = [...selectedUserIds, user.seqId];
            const newSelectedUsers = [...selectedUsers, user];
            onSelectionChange(newUserIds);
            setSelectedUsers(newSelectedUsers);
        }
        setSearchQuery("");
        setSearchResults([]);
        setShowResults(false);
    };

    // 사용자 제거
    const handleRemoveUser = (userId: number) => {
        const newUserIds = selectedUserIds.filter((id) => id !== userId);
        const newSelectedUsers = selectedUsers.filter((user) => user.seqId !== userId);
        onSelectionChange(newUserIds);
        setSelectedUsers(newSelectedUsers);
    };

    return (
        <MDBox>
            <MDTypography variant="body2" color="text" mb={1}>
                특정 사용자 선택 ({selectedUsers.length}명)
            </MDTypography>

            {/* 검색 입력 */}
            <Box position="relative" mb={2}>
                <TextField
                    fullWidth
                    placeholder="사용자 이름 또는 전화번호로 검색"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => searchQuery && setShowResults(true)}
                    InputProps={{
                        endAdornment: loading && <CircularProgress size={20} />,
                    }}
                    sx={{
                        "& .MuiInputBase-root": {
                            backgroundColor: darkMode ? "#2d3748" : "#fff",
                            color: darkMode ? "#fff" : "#000",
                        },
                    }}
                />

                {/* 검색 결과 드롭다운 */}
                {showResults && (
                    <Paper
                        sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            maxHeight: 300,
                            overflow: "auto",
                            zIndex: 1400,
                            mt: 0.5,
                            backgroundColor: darkMode ? "#2d3748" : "#fff",
                        }}
                    >
                        {searchResults.length > 0 ? (
                            <List>
                                {searchResults.map((user) => (
                                    <ListItem key={user.seqId} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleSelectUser(user)}
                                            disabled={selectedUserIds.includes(user.seqId)}
                                        >
                                            <ListItemText
                                                primary={user.name}
                                                secondary={user.mobile || "전화번호 없음"}
                                                sx={{
                                                    "& .MuiListItemText-primary": {
                                                        color: darkMode ? "#fff" : "#000",
                                                        fontWeight: 500,
                                                    },
                                                    "& .MuiListItemText-secondary": {
                                                        color: darkMode ? "#a0aec0" : "#666",
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Box p={2}>
                                <Typography
                                    variant="body2"
                                    color={darkMode ? "#a0aec0" : "text.secondary"}
                                    textAlign="center"
                                >
                                    {loading ? "검색 중..." : "검색 결과가 없습니다"}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                )}
            </Box>

            {/* 선택된 사용자 목록 */}
            {selectedUsers.length > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        p: 2,
                        backgroundColor: darkMode ? "#2d3748" : "#f7fafc",
                        borderRadius: 1,
                    }}
                >
                    {selectedUsers.map((user) => (
                        <Chip
                            key={user.seqId}
                            label={`${user.name} (${user.mobile || "전화번호 없음"})`}
                            onDelete={() => handleRemoveUser(user.seqId)}
                            sx={{
                                backgroundColor: darkMode ? "#4a5568" : "#e2e8f0",
                                color: darkMode ? "#fff" : "#000",
                                "& .MuiChip-deleteIcon": {
                                    color: darkMode ? "#a0aec0" : "#666",
                                },
                            }}
                        />
                    ))}
                </Box>
            )}

            {selectedUsers.length === 0 && (
                <Typography
                    variant="body2"
                    color={darkMode ? "#a0aec0" : "text.secondary"}
                    textAlign="center"
                    sx={{ p: 2 }}
                >
                    사용자를 검색하여 선택해주세요
                </Typography>
            )}
        </MDBox>
    );
}
